import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FiAlertTriangle, FiHelpCircle } from "react-icons/fi";
import { shakeModal } from "../utils/modalAttention";
import s from "./ConfirmDialog.module.css";

/**
 * Globalny, ładny dialog potwierdzenia — zamiennik natywnego window.confirm().
 * Port 1:1 z mPracownika.
 *
 * Użycie:
 *   const confirm = useConfirm();                       // hook zwraca FUNKCJĘ, nie obiekt
 *   if (!(await confirm({ message: "Na pewno usunąć?", danger: true, confirmText: "Usuń" }))) return;
 *   if (await confirm("Krótkie pytanie?")) { ... }      // przeciążenie stringiem
 *
 * Zwraca Promise<boolean> (nigdy nie jest odrzucany). Zbudowany na portalu,
 * więc działa z dowolnego komponentu funkcyjnego.
 *
 * Zamknięcie WYŁĄCZNIE przyciskami (W-9 design systemu) — klik w tło daje potrząśnięcie,
 * brak obsługi Escape.
 */
const ConfirmContext = createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((opts = {}) => {
        const options = typeof opts === "string" ? { message: opts } : opts;
        return new Promise((resolve) => {
            // Tylko jeden dialog naraz. Gdyby ktoś wywołał confirm() przed rozstrzygnięciem
            // poprzedniego — domykamy stary jako "anuluj", żeby nie zostawić wiszącego await.
            if (resolverRef.current) {
                resolverRef.current(false);
            }
            resolverRef.current = resolve;
            setState({
                title: options.title || "Potwierdzenie",
                message: options.message || "Czy na pewno chcesz kontynuować?",
                confirmText: options.confirmText || "Potwierdź",
                cancelText: options.cancelText || "Anuluj",
                danger: options.danger ?? false,
            });
        });
    }, []);

    const close = useCallback((result) => {
        setState(null);
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state &&
                createPortal(
                    <div className={s.overlay} onClick={shakeModal}>
                        <div
                            className={s.dialog}
                            onClick={(e) => e.stopPropagation()}
                            role="alertdialog"
                            aria-modal="true"
                            aria-labelledby="cfm-title"
                            aria-describedby="cfm-msg"
                        >
                            <div className={`${s.iconWrap} ${state.danger ? s.iconDanger : s.iconInfo}`}>
                                {state.danger ? <FiAlertTriangle size={22} /> : <FiHelpCircle size={22} />}
                            </div>
                            <h3 id="cfm-title" className={s.title}>
                                {state.title}
                            </h3>
                            <p id="cfm-msg" className={s.message}>
                                {state.message}
                            </p>
                            <div className={s.actions}>
                                <button type="button" className={s.cancelBtn} onClick={() => close(false)}>
                                    {state.cancelText}
                                </button>
                                <button
                                    type="button"
                                    className={`${s.confirmBtn} ${state.danger ? s.confirmDanger : ""}`}
                                    onClick={() => close(true)}
                                    autoFocus
                                >
                                    {state.confirmText}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within ConfirmDialogProvider");
    return ctx;
};
